import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Tooltip,
  Box,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import BusinessIcon from '@mui/icons-material/Business';
import { Organisation } from '../../../../api';
import { INFINIWELL_ORG_ID } from '../../../../common/constants';

interface OrganisationsTableProps {
  organisations: Organisation[];
  onDelete: (organisationId: string) => void;
  onViewDetails: (organisationId: string) => void;
}

const OrganisationsTable: React.FC<OrganisationsTableProps> = ({
  organisations,
  onDelete,
  onViewDetails,
}) => {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getTierColor = (tier?: string) => {
    switch (tier) {
      case 'enterprise':
        return 'primary';
      case 'professional':
        return 'secondary';
      case 'basic':
      default:
        return 'default';
    }
  };

  const isInfiniwellOrg = (orgId: string) => {
    return orgId === INFINIWELL_ORG_ID;
  };

  if (organisations.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <BusinessIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h6" color="text.secondary">
          No organisations found
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Create your first organisation to get started
        </Typography>
      </Paper>
    );
  }

  return (
    <TableContainer component={Paper}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell><strong>Name</strong></TableCell>
            <TableCell><strong>Domain</strong></TableCell>
            <TableCell><strong>Contact</strong></TableCell>
            <TableCell><strong>Tier</strong></TableCell>
            <TableCell align="center"><strong>Active</strong></TableCell>
            <TableCell align="center"><strong>Users / Guidelines</strong></TableCell>
            <TableCell><strong>Created</strong></TableCell>
            <TableCell align="right"><strong>Actions</strong></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {organisations.map((org) => (
            <TableRow
              key={org.organisation_id}
              hover
              onClick={() => onViewDetails(org.organisation_id)}
              sx={{ cursor: 'pointer' }}
            >
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" fontWeight={600}>
                    {org.name}
                  </Typography>
                  {isInfiniwellOrg(org.organisation_id) && (
                    <Chip
                      label="Superadmin"
                      size="small"
                      color="warning"
                      sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                  )}
                </Box>
                {org.description && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    {org.description}
                  </Typography>
                )}
              </TableCell>
              <TableCell>
                <Typography variant="body2">{org.domain || '—'}</Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                  {org.contact_email || '—'}
                </Typography>
              </TableCell>
              <TableCell>
                <Chip
                  label={org.subscription_tier || 'basic'}
                  size="small"
                  color={getTierColor(org.subscription_tier)}
                />
              </TableCell>
              <TableCell align="center">
                <Chip
                  label={org.is_active ? 'Active' : 'Inactive'}
                  size="small"
                  color={org.is_active ? 'success' : 'default'}
                />
              </TableCell>
              <TableCell align="center">
                <Typography variant="body2">
                  {typeof org.user_count !== 'undefined' ? org.user_count : org.max_users || 0} / {typeof org.guideline_count !== 'undefined' ? org.guideline_count : org.max_guidelines || 0}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2">{formatDate(org.created_at)}</Typography>
              </TableCell>
              <TableCell align="right">
                <Tooltip title="View Details">
                  <IconButton
                    size="small"
                    onClick={(e) => { e.stopPropagation(); onViewDetails(org.organisation_id); }}
                    color="primary"
                  >
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={isInfiniwellOrg(org.organisation_id) ? "Cannot delete Infiniwell organisation" : "Delete"}>
                  <span>
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); onDelete(org.organisation_id); }}
                      color="error"
                      disabled={isInfiniwellOrg(org.organisation_id)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default OrganisationsTable;
